import { Store } from 'vuex';

import { AppState } from '@/store';
import { MUTATIONS as customerMutations } from '@/store/customer';
import { LoyaltyService } from '@/services/loyalty/loyalty.service';
import { sleep } from '@/helpers/util';
import { ServicesContainer } from '@/services';
import { SeonService } from '../seon/seon.service';

declare global {
  interface Window {
    ameliaChatOverlayConfig?: any;
  }
}

export interface PepperChatService {
  openChatWindow(): void;
  endConversation(): Promise<void>;
  sendCustomerInfo(): void;
}

export class AppPepperChatService implements PepperChatService {
  constructor(private store: Store<AppState>, private loyaltyService: LoyaltyService, private seonService: SeonService) {
    /**
     * This is the easiest way given the existing code to ensure customer profile changes are synced to LP for Pepper chatbot
     * See "TODO-d8ehf5" in "axios-factory.ts" for the rationale behind having separate mutations for changing the profile.
     */
    store.subscribe(mutation => {
      switch (mutation.type) {
        case customerMutations.RESET_PROFILE:
          this.endConversation();
          break;
        case customerMutations.REFRESH_PROFILE:
          this.sendCustomerInfo();
          break;
        case customerMutations.SET_CUSTOMER_PROFILE:
          mutation.payload === null ? this.endConversation() : this.sendCustomerInfo();
      }
    });

    window.addEventListener('message', async event => {
      if (event.data?.notificationType === 'conversationJoined' ||
        event.data?.notificationType === 'conversationNew') {
        await this.postMessageToIframe();
      }
    });

    this.seonService.loadLibrary();
  }

  async getDynamicMessageData() {
    const { profile } = this.store.state.customer;
    const lastOrder = (this.store.state.order.lastOrder.order?.order as { orderShortCode?: string }) || {};
    const sessionPromise = await this.seonService.getSession();
    const sde = {
      firstname: profile?.firstName ?? null,
      lastname: profile?.lastName ?? null,
      email: profile?.email ?? null,
      phone: profile?.phoneNumber ?? null,
      isGuestUser: profile?.isGuest === undefined ? 'true' : JSON.stringify(profile?.isGuest),
      isLoyaltyMember: JSON.stringify(this.loyaltyService.isEnrolledInLoyalty(profile)),
      sessionHash: sessionPromise,
      channel: 'web',
      jwt: this.store.state.customer.jwt,
      ...(lastOrder?.orderShortCode != null && { orderId: lastOrder.orderShortCode }),
    };
    return sde;
  }

  async postMessageToIframe() {
    const iframe = document.getElementById('amelia-chat-overlay-iframe') as HTMLIFrameElement | null;
    const messageData = await this.getDynamicMessageData();
    const iframeSrc = iframe?.getAttribute("src");
    if (iframe && iframe.contentWindow && iframeSrc) {
      iframe.contentWindow.postMessage(
        {
          actionType: 'ameliaConversationAttributes',
          actionData: messageData,
        },
        iframeSrc
      );
    }
  }

  openChatWindow(): void {
    const iframeId = 'amelia-chat-overlay-iframe';
    if (document.getElementById(iframeId)) {
      const button = document.querySelector('.amelia-chat-overlay-floating-button') as HTMLElement | null;
      if (button) {
        button.click();
      }
      return;
    }
  }

  async endConversation(): Promise<void> {
    this.hideChatWindow();
  }

  hideChatWindow(): void {
    const iframe = document.getElementById('amelia-chat-overlay-iframe') as HTMLIFrameElement | null;
    if (iframe) {
      iframe.style.display = 'none';
    }
    const container = document.getElementById('amelia-chat-overlay-container') as HTMLElement | null;
    if (container) {
      container.style.display = 'none';
    }
  }

  sendCustomerInfo(): void {
    this.postMessageToIframe();
  }
}
